import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import styles from '../../styles/mariana-scroll-arrows.css?inline';

/**
 * Scroll Arrows Component for Card Carousels (Desktop Only)
 * Displays left/right navigation arrows overlaying the card carousel
 * Visible only on screens wider than 769px
 * Hidden when there is only 1 card (same logic as dots)
 */
@customElement('mariana-scroll-arrows')
export class MarianaScrollArrows extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @property({ type: Number })
  totalCards: number = 0;

  @property({ type: Number })
  currentIndex: number = 0;

  @state()
  private parentHovered: boolean = false;

  private parentEl: Element | null = null;
  private onParentEnter = () => { this.parentHovered = true; };
  private onParentLeave = () => { this.parentHovered = false; };

  connectedCallback(): void {
    super.connectedCallback();
    this.parentEl = this.parentElement;
    if (this.parentEl) {
      this.parentEl.addEventListener('mouseenter', this.onParentEnter);
      this.parentEl.addEventListener('mouseleave', this.onParentLeave);
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.parentEl) {
      this.parentEl.removeEventListener('mouseenter', this.onParentEnter);
      this.parentEl.removeEventListener('mouseleave', this.onParentLeave);
      this.parentEl = null;
    }
  }

  private handleArrowClick(direction: 'left' | 'right') {
    const newIndex = direction === 'left'
      ? this.currentIndex - 1
      : this.currentIndex + 1;

    this.dispatchEvent(
      new CustomEvent('arrow-click', {
        detail: { index: newIndex, direction },
        bubbles: true,
        composed: true,
      })
    );
  }

  render() {
    // Don't render arrows if 1 or fewer cards
    if (this.totalCards <= 1) {
      return html``;
    }

    const isAtStart = this.currentIndex <= 0;
    const isAtEnd = this.currentIndex >= this.totalCards - 1;

    return html`
      <button
        class="scroll-arrow scroll-arrow-left ${isAtStart ? 'hidden' : ''} ${this.parentHovered ? 'visible' : ''}"
        @click=${() => this.handleArrowClick('left')}
        type="button"
        aria-label="Previous card"
        ?disabled=${isAtStart}
      >
        <div class="arrow-icon arrow-icon-left"></div>
      </button>
      <button
        class="scroll-arrow scroll-arrow-right ${isAtEnd ? 'hidden' : ''} ${this.parentHovered ? 'visible' : ''}"
        @click=${() => this.handleArrowClick('right')}
        type="button"
        aria-label="Next card"
        ?disabled=${isAtEnd}
      >
        <div class="arrow-icon arrow-icon-right"></div>
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mariana-scroll-arrows': MarianaScrollArrows;
  }
}
